import { Box } from '@mui/material';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import appPhones from '../../../assets/images/signin/app-phones.png';
import execLogosKsa from '../../../assets/images/signin/exec-logos-ksa.png';
import execLogosUae from '../../../assets/images/signin/exec-logos-uae.png';
import appQr from '../../../assets/images/signin/qr.png';
import storeBadges from '../../../assets/images/signin/store-badges-white.png';
import { OpenPanelEvents } from '../../../constants/openPanelEvents';
import { trackCustomerIo } from '../../../helpers/customerIoHelper';
import { getSignInAsideContent } from './signInAsideContent';

const EXEC_LOGOS = { uae: execLogosUae, ksa: execLogosKsa };
const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.fundingsouq';
const APP_STORE_URL = 'https://apps.apple.com/us/app/funding-souq/id6739612948';

const Rule = ({ className = '' }) => (
	<span className={`flex-1 h-px bg-white/75 ${className}`} />
);

// Right-hand green panel of the Sign In page. Shell: the region decision
// lives in getSignInAsideContent, this only renders it.
export const SignInAside = ({ isSA }) => {
	const { t } = useTranslation();
	const { logoStrip } = getSignInAsideContent(isSA);
	const region = isSA ? 'ksa' : 'uae';
	const textDir = i18next.language === 'ar' ? 'rtl' : 'ltr';

	const trackBadge = (event, platform) => () =>
		trackCustomerIo(event, { platform, page: 'login', region });

	return (
		<Box
			dir="ltr"
			data-testid="signInAside"
			className="primary-bg text-white hidden md:flex flex-col px-4 pt-14 pb-[26px]">
			<h2
				dir={textDir}
				className="!text-[26px] !leading-tight !font-bold text-center !mb-12">
				{t('signInTrustedBy')}
			</h2>

			<Box className="grid grid-cols-[5fr_6fr] items-center gap-4">
				<Box className="flex flex-col items-center">
					<Box
						dir={textDir}
						className="!text-[17px] xl:!text-[20px] !leading-[1.35] !font-semibold text-center !mb-4 max-w-[160px] xl:max-w-[190px]">
						{t('signInFasterOnApp')}
					</Box>
					<img
						className="w-[150px] xl:w-[190px] h-auto rounded-[6px] block"
						src={appQr}
						alt="QR code to download the Funding Souq app"
					/>
					<Box className="w-full max-w-[220px] xl:max-w-[270px] flex items-center gap-3 !my-4">
						<Rule />
						<span dir={textDir} className="text-[12px]">{t('or')}</span>
						<Rule />
					</Box>
					<Box className="relative w-full max-w-[220px] xl:max-w-[270px]">
						<img
							className="w-full h-auto block"
							src={storeBadges}
							alt="Get it on Google Play, Download on the App Store"
						/>
						<a
							data-testid="signInGooglePlayLink"
							data-event="google_play_button_clicked"
							className="absolute inset-y-0 left-0 w-1/2"
							target="_blank"
							rel="noreferrer"
							aria-label="Get it on Google Play"
							href={GOOGLE_PLAY_URL}
							onClick={trackBadge(OpenPanelEvents.googlePlayButtonClicked, 'android')}
						/>
						<a
							data-testid="signInAppStoreLink"
							data-event="app_store_button_clicked"
							className="absolute inset-y-0 right-0 w-1/2"
							target="_blank"
							rel="noreferrer"
							aria-label="Download on the App Store"
							href={APP_STORE_URL}
							onClick={trackBadge(OpenPanelEvents.appStoreButtonClicked, 'ios')}
						/>
					</Box>
				</Box>
				<img
					className="w-full max-w-[400px] h-auto block justify-self-center"
					src={appPhones}
					alt="Funding Souq app screens"
				/>
			</Box>

			<Box className="flex items-center gap-[14px] mt-12 mb-[18px]">
				<Rule />
				<span dir={textDir} className="text-[14.5px] font-semibold whitespace-nowrap">
					{t('backedByInvestors')}
				</span>
				<Rule />
			</Box>
			<img
				data-testid="signInExecLogos"
				className="w-full h-auto block"
				src={EXEC_LOGOS[logoStrip]}
				alt=""
			/>

			<Box className="flex-1 min-h-10" />
			<Box
				dir={textDir}
				className="flex items-center justify-center gap-2 flex-wrap text-[12.5px] font-semibold">
				<span>{t('regulatedBySAMA')}</span>
				<span>•</span>
				<span>{t('verifiedBySMEs')}</span>
				<span>•</span>
				<span>{t('shariahCompliantSignUp')}</span>
			</Box>
		</Box>
	);
};
